import { AbyssTheme as themeConfiguration } from '@src/app/browser';
import { useApi } from '@src/app/context/Api';
import { useSaveCommonCriteria } from '@src/entities/Criteria/ui/hooks/useSaveCommonCriteria';
import { fieldValidator } from '@src/shared/includes/validation';
import { Button } from '@src/shared/ui/components/Button';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useDebounce } from '@src/shared/ui/hooks';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { isUndefined } from 'lodash';
import React, { useCallback, useEffect } from 'react';

import fields from '../includes/fields.json';
import { Styles } from './includes/styles';

const { name: fieldName } = fields;

/**
 * Field: Name
 *
 * This field is used to display the name of the entity.
 *
 * @param currentEntity
 * @param form
 * @param refetch
 * @param showButton
 * @param showLabel
 * @returns {Element}
 * @constructor
 */
export function Name({ currentEntity = {}, form = {}, refetch = () => {}, showButton = true, showLabel = false }) {
  const name = form.getValues('name');

  const handleSave = useSaveCommonCriteria('saveName');

  const { useApiQuery } = useApi('Criteria');

  const [GetValidCriteriaName, { data, isFetching, isLoading }] = useApiQuery('GetValidCriteriaName', {
    staleTime: 0,
  });

  /**
   * sendRequest
   *
   * Makes a request to a remote API to validateCriteria whether the user provided action path name exists or not.
   *
   * @type {(function(*): Promise<void>)|*}
   */
  const sendRequest = useCallback(async () => {
    if ((await form?.trigger('name')) === true && name !== currentEntity?.name) {
      GetValidCriteriaName({ name });
    }
  }, [name]);

  /**
   * With the API response, if the name is valid, clear the error, otherwise set the name in use error.
   */
  useEffect(() => {
    if (!isUndefined(data)) {
      const isValid = data;

      if (isValid === true) {
        form?.clearErrors('name');
      }

      if (isValid === false) {
        form?.setError('name', { message: 'Name already in use.', type: 'custom' }, { shouldFocus: true });
      }
    }
  }, [data, isLoading, isFetching]);

  /**
   * When the name changes, validate the field.
   */
  useEffect(() => {
    form.validate(
      'name',
      () => {},
      () => {}
    );
  }, [name, currentEntity?.name]);

  const debouncedRequest = useDebounce(sendRequest, 250);

  return (
    <ErrorHandler location="src/entities/Criteria/ui/components/fields/Name/Name.jsx">
      <Styles>
        <TextInput
          {...fieldName}
          hideLabel={!showLabel}
          inputRightElement={
            showButton ? (
              <Button
                onClick={async () => {
                  await handleSave({ ...currentEntity, ...{ name } }, refetch);
                }}
                variant="solid"
              >
                Save
              </Button>
            ) : (
              <React.Fragment />
            )
          }
          onChange={async (event) => {
            if (event?.target?.value?.length >= 3) {
              if ((await form?.trigger('name')) === true) {
                form?.register('name');
                form?.setError(
                  'name',
                  { message: 'Verifying name is available...', type: 'custom' },
                  { shouldFocus: true }
                );
              }
              debouncedRequest();
            }
          }}
          validators={{
            ...fieldName.validators,
            ...{
              validate: {
                customValidator: (value) => {
                  return fieldValidator(fieldName, value);
                },
              },
            },
          }}
          width={`${Number(themeConfiguration?.theme?.space?.xl?.replace('px', '')) * 8}px`}
        />
      </Styles>
    </ErrorHandler>
  );
}
